import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { storeConfig } from '../../../../store.config'
import { ROUTES } from '../../../constants/routes.constants'
import { StorefrontBackdrop } from '../storefront/chrome'

/**
 * Top-aligned card layout for /account/login, /account/register,
 * /account/forgot-password, /account/reset-password.
 *
 * Mobile scroll fix (Kiran 2026-05-14, same root cause as the Modal
 * atom): previously used `min-h-screen flex items-center justify-center`
 * which clips the top + bottom of the card on phones whenever the
 * form is taller than the viewport (you can't scroll up to the
 * Sign Up / Sign In button because flex-center pins the card to the
 * middle and the overflow at the top is unreachable). Now the
 * container is plain block flow with `min-h-[100dvh]` (dynamic vh,
 * so the mobile URL bar collapsing doesn't truncate) and the card
 * sits with `mx-auto` so horizontal centering still works. Vertical
 * centering on tall desktops is intentionally given up — a card
 * pinned a bit below the top is fine, and it's always scrollable.
 */
export function AuthLayout({ children }: { readonly children: ReactNode }): JSX.Element {
  const brand = storeConfig.brand
  return (
    <div className="relative min-h-[100dvh] overflow-x-hidden px-4 py-8 sm:py-12">
      <div className="fixed inset-0 z-0">
        <StorefrontBackdrop />
      </div>
      <div className="relative z-10 max-w-md mx-auto flex flex-col items-center">
        <Link to={ROUTES.HOME} className="flex items-baseline gap-2 mb-6 sm:mb-8">
          {brand.nameScript !== null ? (
            <span className="font-kannada text-h1 text-forest">{brand.nameScript}</span>
          ) : null}
          <span className="font-heading text-h2 text-charcoal">{brand.name}</span>
        </Link>
        <div className="w-full bg-bg-primary/95 rounded-lg shadow-lg p-6 sm:p-8 border border-border backdrop-blur-sm">
          {children}
        </div>
      </div>
    </div>
  )
}
